import { useEffect, useMemo, useState } from "react";
import { Check, Plus, Sparkles, Trash2, X } from "lucide-react";

import { useModalContext } from "../components/ModalProvider";
import {
  SynonymCollection,
  SynonymEntry,
  SynonymRow,
  useSynonymsAPI,
} from "../SynonymsAPI";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const statusVariant = (s: SynonymEntry["status"]): "success" | "warning" | "outline" =>
  s === "approved" ? "success" : s === "proposed" ? "warning" : "outline";

/**
 * HITL admin for the generic synonym mechanism. Synonyms are alternative NAMES of a canonical value;
 * this screen approves/rejects/edits them and can trigger LLM proposals. Writes only the `synonyms`
 * field (portal-owned); canonical fields stay seed-owned.
 */
export default function SynonymsPanel() {
  const api = useSynonymsAPI();
  const { pushError } = useModalContext() as any;

  const [collections, setCollections] = useState<SynonymCollection[]>([]);
  const [collection, setCollection] = useState<string>("");
  const [rows, setRows] = useState<SynonymRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [addTerm, setAddTerm] = useState<Record<string, string>>({});
  /** Hochgezählt, wenn etwas geschrieben wurde, das die Liste verändert — der Effekt lädt dann neu. */
  const [reloadSignal, setReloadSignal] = useState(0);

  useEffect(() => {
    api
      .getCollections()
      .then((r) => {
        setCollections(r.collections);
        if (r.collections.length && !collection) setCollection(r.collections[0].collection);
      })
      .catch(pushError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Die Zeilen zur gewählten Sammlung, mit Schutz gegen die überholte Antwort.
   *
   * Wer die Sammlung wechselt oder weitertippt, während die vorige Anfrage noch läuft, bekommt
   * zwei Antworten in unbestimmter Reihenfolge — und die langsamere, ältere schreibt zuletzt. Die
   * Marke `cancelled` gehört deshalb in den Effekt und wird von seiner Aufräumfunktion gesetzt:
   * Das ist der einzige Ort, den React vor dem nächsten Durchlauf ausführt.
   *
   * Ein `AbortController` wäre das schärfere Werkzeug, aber nur, wenn sein Signal auch bis in die
   * Anfrage reicht. `getEntities` nimmt keins entgegen; einer, den niemand übergibt, bricht nichts
   * ab und prüft sich selbst gegen ein Feld, das nie gesetzt wird.
   */
  useEffect(() => {
    if (!collection) return;

    let cancelled = false;
    setLoading(true);

    api
      .getEntities(collection, { search: search.trim() || undefined, limit: 500 })
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setTotal(r.total);
      })
      .catch((error) => {
        if (!cancelled) pushError(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, search, reloadSignal]);

  const applyLocal = (key: string, entity: SynonymRow) =>
    setRows((prev) => prev.map((r) => (r.key === key ? entity : r)));

  const review = async (key: string, op: any, term: string, extra: any = {}): Promise<boolean> => {
    try {
      const { entity } = await api.review(collection, { key, op, term, ...extra });
      applyLocal(key, entity);
      return true;
    } catch (e) {
      pushError(e);
      return false;
    }
  };

  const addSynonym = async (key: string) => {
    const term = (addTerm[key] || "").trim();
    if (!term) return;
    const success = await review(key, "add", term);
    if (success) {
      setAddTerm((s) => ({ ...s, [key]: "" }));
    }
  };

  const generate = async () => {
    if (!collection) return;
    try {
      setGenerating(true);
      const r = await api.generate(collection, {});
      toast.success(
        `${r.proposalsAdded} Vorschläge über ${r.objectsUpdated} Werte; ${r.shared?.length ?? 0} mehrdeutig (nicht vergeben).`,
      );
      setReloadSignal((signal) => signal + 1);
    } catch (e) {
      pushError(e);
    } finally {
      setGenerating(false);
    }
  };

  const counts = useMemo(() => {
    let proposed = 0;
    let approved = 0;
    for (const r of rows) {
      for (const s of r.synonyms) {
        if (s.status === "approved") approved++;
        else if (s.status === "proposed") proposed++;
      }
    }
    return { proposed, approved };
  }, [rows]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Synonyme</h2>
        <Select value={collection} onValueChange={setCollection}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Collection wählen" />
          </SelectTrigger>
          <SelectContent>
            {collections.map((c) => (
              <SelectItem key={c.collection} value={c.collection}>
                {c.collection}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="search"
          placeholder="Suche (Name / Synonym / Key)"
          className="w-[280px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="outline" size="sm" onClick={generate} disabled={generating || !collection}>
          <Sparkles className="mr-2 h-4 w-4" />
          {generating ? "Generiere…" : "Vorschläge generieren"}
        </Button>
        <div className="ml-auto text-sm text-muted-foreground">
          {total} Werte · {counts.approved} freigegeben · {counts.proposed} vorgeschlagen
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[280px]">Wert</TableHead>
                <TableHead>Synonyme (alternative Namen)</TableHead>
                <TableHead className="w-[280px]">Hinzufügen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="align-top">
                    <div className="font-medium">{r.label}</div>
                    <div className="text-xs text-muted-foreground">{r.key}</div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap gap-1.5">
                      {r.synonyms.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                      {r.synonyms.map((s) => (
                        <span key={s.term} className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5">
                          <Badge variant={statusVariant(s.status)} title={s.rationale || s.source}>
                            {s.term}
                          </Badge>
                          {s.status !== "approved" && (
                            <button title="Freigeben" onClick={() => review(r.key, "approve", s.term)}>
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            </button>
                          )}
                          {s.status !== "rejected" && (
                            <button title="Ablehnen" onClick={() => review(r.key, "reject", s.term)}>
                              <X className="h-3.5 w-3.5 text-amber-600" />
                            </button>
                          )}
                          <button title="Entfernen" onClick={() => review(r.key, "remove", s.term)}>
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex gap-2">
                      <Input
                        placeholder="neues Synonym"
                        value={addTerm[r.key] || ""}
                        onChange={(e) => setAddTerm((st) => ({ ...st, [r.key]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && addSynonym(r.key)}
                      />
                      <Button size="sm" variant="secondary" onClick={() => addSynonym(r.key)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
